import { memo, useContext, useEffect, useState, useRef, useMemo, useCallback } from 'react';
import './style.css';

// Icons & Components
import { BsFillCartCheckFill } from 'react-icons/bs';
import { FaClone } from 'react-icons/fa';
import { Switch } from '@mui/material';

import NewProduct from '../newProduct/view';
import EditProduct from '../editProduct/view';
import Table from '../../molecules/tablas/view';
import ButtonAction from '../../atoms/buttonAction/view';
import ConfirmPopUp from '../../atoms/confirmPopUp/view';
import { Trash2, Boxes, Coins, ShieldCheck, ToggleLeft } from 'lucide-react';
import FacetedFilter from '../../molecules/FacetedFilter/view';

// Utils & Domain
import { EmptyArray } from '../../../domain/empty/model';
import { getAllProducts, isEnable, deleteProduct } from '../../../usecases/products-usecase';
import { searchCatalog } from '../../../usecases/catalog-usecase';
import { dispatchCustomEvent, listener } from '../../../utils/dispatch';
import { devicecontext } from '../../../context/device/context';
import { mapProductForTable, mapProductsListForTable } from '../../../infrastructure/mappers/product-mapper';
import { mapCatalogListForTable } from '../../../infrastructure/mappers/catalog-mapper';

// Controller
import { setUpTable } from './controller';

const IsEnable = memo(({ data, profile }) => {
    const is_enable = data.enable ?? true;

    const changeEnable = (id, check) => {
        isEnable(profile?.uid, profile?.cid, id, check);
    };

    return <Switch defaultChecked={is_enable} onChange={(event) => changeEnable(data.id, event.target.checked)} />;
});

const DeleteProductBtn = memo(({ data }) => {
    return (
        <ButtonAction
            id={`delete-product-${data?.sku}`}
            btnKey="delete-product"
            onClick={() => dispatchCustomEvent('btn-notification-confirm-delete-product', data)}
            title={<Trash2 size={14} />}
            style={{ width: '100%' }}
        />
    );
});

const TaxChip = memo(({ data }) => {
    const hasTax = data?.has_tax ?? false;
    const text = hasTax ? "Gravado" : "Exento";
    const styles = hasTax ? {
        backgroundColor: 'rgba(16, 185, 129, 0.15)',
        color: '#10b981',
        border: '1px solid rgba(16, 185, 129, 0.3)',
    } : {
        backgroundColor: 'rgba(156, 163, 175, 0.15)',
        color: '#9ca3af',
        border: '1px solid rgba(156, 163, 175, 0.3)',
    };

    return (
        <span style={{
            padding: '4px 10px',
            borderRadius: '20px',
            fontSize: '0.75rem',
            fontWeight: '600',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            ...styles
        }}>
            {text}
        </span>
    );
});

const WarrantyChip = memo(({ data }) => {
    const warranty = data?.warranty;
    const hasWarranty = warranty && warranty !== 'Sin Garantía' && warranty.trim() !== '';
    const text = hasWarranty ? warranty : "Sin Garantía";
    const styles = hasWarranty ? {
        backgroundColor: 'rgba(59, 130, 246, 0.15)',
        color: '#3b82f6',
        border: '1px solid rgba(59, 130, 246, 0.3)',
    } : {
        backgroundColor: 'rgba(156, 163, 175, 0.15)',
        color: '#9ca3af',
        border: '1px solid rgba(156, 163, 175, 0.3)',
    };

    return (
        <span style={{
            padding: '4px 10px',
            borderRadius: '20px',
            fontSize: '0.75rem',
            fontWeight: '600',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            ...styles
        }}>
            {text}
        </span>
    );
});

function ProductsTable({ ref, type = "sell", profile, initialFilter = {}, scannerMode = "filter" }) {
    const [activeFilters, setActiveFilters] = useState([]);
    const [appliedFilters, setAppliedFilters] = useState({
        hasTax: null,
        stockStatus: null,
        hasWarranty: null,
        enable: null,
    });

    const [productsData, setProductsData] = useState({
        data: [],
        page: 1,
        page_size: 50,
        total: 0,
    });
    const [query, setQuery] = useState("");
    const [loading, setLoading] = useState(false);

    const setup = useMemo(() => setUpTable(type), [type]);
    const scannerBuffer = useRef('');
    const lastScanTime = useRef(Date.now());

    const filtersConfig = useMemo(() => {
        if (type !== 'stock') return [];
        return [
            {
                key: 'stockStatus',
                label: 'Disponibilidad',
                icon: <Boxes size={14} />,
                type: 'select',
                options: [
                    { value: 'with_stock', label: 'Con Stock' },
                    { value: 'out_of_stock', label: 'Sin Stock (Agotado)' },
                    { value: 'negative_stock', label: 'Stock Negativo' }
                ]
            },
            {
                key: 'hasTax',
                label: 'Impuestos',
                icon: <Coins size={14} />,
                type: 'select',
                options: [
                    { value: 'true', label: 'Gravados' },
                    { value: 'false', label: 'Exentos' }
                ]
            },
            {
                key: 'hasWarranty',
                label: 'Garantía',
                icon: <ShieldCheck size={14} />,
                type: 'select',
                options: [
                    { value: 'true', label: 'Con Garantía' },
                    { value: 'false', label: 'Sin Garantía' }
                ]
            },
            {
                key: 'enable',
                label: 'Estado',
                icon: <ToggleLeft size={14} />,
                type: 'select',
                options: [
                    { value: 'true', label: 'Activos' },
                    { value: 'false', label: 'Inactivos' }
                ]
            }
        ];
    }, [type]);

    useEffect(() => {
        if (type !== 'stock') return;
        const filters = {
            hasTax: null,
            stockStatus: null,
            hasWarranty: null,
            enable: null,
        };
        activeFilters.forEach(f => {
            if (f.key === 'hasTax') {
                filters.hasTax = f.value === 'true';
            }
            if (f.key === 'stockStatus') {
                filters.stockStatus = f.value;
            }
            if (f.key === 'hasWarranty') {
                filters.hasWarranty = f.value === 'true';
            }
            if (f.key === 'enable') {
                filters.enable = f.value === 'true';
            }
        });
        setAppliedFilters(filters);
    }, [activeFilters, type]);

    const fetchProducts = useCallback(async (
        pageNumber = productsData.page, 
        limit = productsData.page_size, 
        searchQuery = query,
        filters = appliedFilters
    ) => {
        setLoading(true);
        try {
            if (type === 'sell') {
                const res = await searchCatalog(
                    profile?.uid,
                    profile?.cid,
                    searchQuery,
                    pageNumber,
                    limit,
                    'all',
                    true
                );

                if (res.total === 0 || !res.data || res.data.length === 0) {
                    setProductsData({
                        data: EmptyArray,
                        page: pageNumber,
                        page_size: limit,
                        total: 0
                    });
                } else {
                    const mappedData = mapCatalogListForTable(res.data);
                    setProductsData({
                        ...res,
                        data: mappedData
                    });
                }
                return;
            }

            const onlyEnabled = type !== 'stock';
            const onlyNegativeStock = initialFilter.onlyNegativeStock ?? false;
            
            const res = await getAllProducts(
                profile?.uid, 
                profile?.cid, 
                searchQuery, 
                pageNumber, 
                limit, 
                true, 
                onlyEnabled, 
                onlyNegativeStock,
                filters.hasTax,
                filters.stockStatus,
                filters.hasWarranty,
                filters.enable
            );
            
            if (res.total === 0 || !res.data || res.data.length === 0) {
                setProductsData({
                    data: EmptyArray,
                    page: pageNumber,
                    page_size: limit,
                    total: 0
                });
            } else {
                const mappedData = mapProductsListForTable(res.data, type);
                setProductsData({
                    ...res,
                    data: mappedData
                });
            }
        } catch (err) {
            setProductsData({
                data: EmptyArray,
                page: pageNumber,
                page_size: limit,
                total: 0
            });
        } finally {
            setLoading(false);
        }
    }, [profile, type, initialFilter, query, productsData.page, productsData.page_size, appliedFilters]);

    const handleSearch = useCallback((val) => {
        setQuery(val);
        fetchProducts(1, productsData.page_size, val, appliedFilters);
    }, [productsData.page_size, fetchProducts, appliedFilters]);

    // ESCÁNER GLOBAL PARA BÚSQUEDA
    useEffect(() => {
        if (scannerMode !== 'filter') return;

        const handleGlobalScan = (e) => {
            if (!e || !e.key) return;

            const isInput = e.target?.tagName === 'INPUT' || e.target?.tagName === 'TEXTAREA';
            const isSearchBar = e.target?.classList?.contains('table-search-input');
            
            const currentTime = Date.now();
            const timeDiff = currentTime - lastScanTime.current;
            lastScanTime.current = currentTime;

            if (e.key === 'Enter') {
                if (scannerBuffer.current?.length > 2) {
                    e.preventDefault();
                    e.stopPropagation();
                    const code = scannerBuffer.current;
                    scannerBuffer.current = '';
                    
                    // Ejecutar búsqueda inmediata
                    setQuery(code);
                    const searchInput = document.querySelector('.table-search-input');
                    if (searchInput) {
                        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value").set;
                        nativeInputValueSetter.call(searchInput, code);
                        const ev = new Event('input', { bubbles: true });
                        searchInput.dispatchEvent(ev);
                    }
                    fetchProducts(1, productsData.page_size, code);
                    
                    dispatchCustomEvent('popup', { message: `Buscando código: ${code}`, type: 'info', duration: 1500 });
                }
            } else if (e.key && e.key.length === 1) {
                // Si no estamos en un input o es la barra de búsqueda o es ráfaga rápida
                if (!isInput || isSearchBar || (scannerBuffer.current && scannerBuffer.current.length > 0) || timeDiff < 50) {
                    
                    // Si el usuario está escribiendo manualmente en la barra de búsqueda, no le robamos las teclas lentas
                    if (isSearchBar && timeDiff > 100 && scannerBuffer.current?.length === 0) {
                        return;
                    }

                    e.preventDefault();
                    e.stopPropagation();
                    scannerBuffer.current = (scannerBuffer.current || '') + e.key;

                    clearTimeout(window.globalScanTimeout);
                    window.globalScanTimeout = setTimeout(() => {
                        scannerBuffer.current = '';
                    }, 300);
                }
            }
        };

        document.addEventListener('keydown', handleGlobalScan, true);
        return () => {
            document.removeEventListener('keydown', handleGlobalScan, true);
            clearTimeout(window.globalScanTimeout);
        };
    }, [fetchProducts, productsData.page_size]);

    // 1. Carga inicial y listeners globales (se ejecuta una sola vez al montar)
    useEffect(() => {
        fetchProducts(1, productsData.page_size, "");

        const onProductEdited = (event) => {
            const updatedProduct = mapProductForTable(event.detail.data, type);
            setProductsData(prev => {
                if (!Array.isArray(prev.data)) return prev;
                const index = prev.data.findIndex(p => p.sku === updatedProduct.sku);
                if (index > -1) {
                    const nextData = [...prev.data];
                    nextData[index] = updatedProduct;
                    return { ...prev, data: nextData };
                }
                return prev;
            });
        };

        const reload = () => fetchProducts(1, productsData.page_size, query, appliedFilters);

        listener('product-edited', reload);
        listener('product-added', reload);
        listener('reload-stock', reload);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // 2. Re-consulta ante cambios de filtros aplicados
    const isFirstRender = useRef(true);
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        fetchProducts(1, productsData.page_size, query, appliedFilters);
    }, [appliedFilters]); // eslint-disable-line react-hooks/exhaustive-deps

    const excludeList = useMemo(() => [
        'id', 'created_at', 'updated_at', 'users_id', 'branch_id', 'description',
        'sell_taxes', 'buy_taxes', 'sell_taxes_id', 'buy_taxes_id', 'barcode', 'unit_of_measure',
        'sell_tax', 'buy_tax', 'type', 'is_service', 'tax', 'price',
        ...(type !== 'stock' ? ['warranty', 'has_tax'] : []),
        ...(type === 'sell' ? ['buy_value', 'stock', 'enable'] : []),
        ...(type === 'buy' ? ['value', 'stock', 'enable'] : [])
    ], [type]);

    const headersList = useMemo(() => {
        if (type === 'stock') return ["sku", "name", "value", "buy_value", "stock", "warranty", "has_tax", "enable"];
        if (type === 'sell') return ["sku", "name", "value"];
        return ["sku", "name", "buy_value"];
    }, [type]);

    const tableFormats = useMemo(() => ({
        value: { type: 'money' },
        buy_value: { type: 'money' },
        warranty: {
            type: "custom",
            children: <WarrantyChip />
        },
        has_tax: {
            type: "custom",
            children: <TaxChip />
        },
        enable: {
            type: "custom",
            children: <IsEnable profile={profile} />
        }
    }), [profile]);

    const handleDeleteProduct = (product) => {
        if (productsData.total <= 1) {
            dispatchCustomEvent('popup', {
                message: 'No puedes eliminar el último producto disponible.',
                type: 'error'
            });
            return;
        }
        deleteProduct(profile.uid, profile.cid, product.id)
            .then(() => {
                dispatchCustomEvent('popup', {
                    message: 'Producto eliminado correctamente',
                    type: 'success',
                });
                fetchProducts(1, productsData.page_size, query);
            })
            .catch((err) => {
                dispatchCustomEvent('popup', {
                    message: err.response?.data?.message || 'Error al eliminar el producto',
                    type: 'error',
                });
            });
    };

    return (
        <>
            <ConfirmPopUp
                eventKey="delete-product"
                msg="¿Estás seguro de eliminar este producto? Esta acción no se puede deshacer."
                onAcept={handleDeleteProduct}
            />
            {type === 'stock' && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
                    <FacetedFilter
                        filters={filtersConfig}
                        activeFilters={activeFilters}
                        onChange={setActiveFilters}
                        onClear={() => setActiveFilters([])}
                    />
                </div>
            )}
            <Table
                id={`products-table-${type}`}
                emptyMessage="Aún no tienes productos registrados"
                emptyChild={<NewProduct id="products-table-empty" profile={profile} />}
                maxHeight="calc(100vh - 430px)"
                headersList={headersList}
                paginatedData={productsData}
                getFunction={(page, limit) => fetchProducts(page, limit, query, appliedFilters)}
                allowExport={true}
                exportFileName={`productos_${type}`}
                allowPageSizeControl={true}
                excludeList={excludeList}
                formats={tableFormats}
                isLoading={loading && (!productsData.data || productsData.data.length === 0)}
                allowSearch={true}
                onSearch={handleSearch}
            >
                {type === 'stock' ? [
                        <EditProduct key="edit" profile={profile} />,
                        <DeleteProductBtn key="delete" />
                ] : [
                        <ButtonAction
                            key="add"
                            title={<BsFillCartCheckFill />}
                            onClick={(product) => dispatchCustomEvent("btn-add-product", product)}
                        />,
                        <ButtonAction
                            key="clone"
                            title={<FaClone />}
                            onClick={(product) => dispatchCustomEvent("btn-clone-product", product)}
                        />
                ]}
            </Table>
        </>
    );
}

export default memo(ProductsTable);
